import { WEBSOCKET_MESSAGE_TYPES } from '../constants';

let globalWs: WebSocket | null = null;

/**
 * Upper bound on events held while the socket is down. Only `durable` events
 * queue (see {@link LogEventOptions}), and those are rare by construction, so
 * this exists purely so a pathological loop can't grow the buffer without
 * limit.
 */
const MAX_QUEUED_EVENTS = 20;

/**
 * How long a queued event stays useful. The service derives `contact_id` from
 * the WebSocket connection rather than the payload, so an event flushed over a
 * socket belonging to a *different* contact would be misattributed. Recovery
 * from the case this queue exists for (auth loss) takes a few seconds, so
 * anything older than this is far more likely to be a stale event from a
 * previous contact than a late-delivered useful one — drop it.
 */
const QUEUED_EVENT_TTL_MS = 60_000;

interface QueuedEvent {
  event_type: string;
  queuedAt: number;
}

let queue: QueuedEvent[] = [];

const serialize = (event_type: string): string =>
  JSON.stringify({
    type: WEBSOCKET_MESSAGE_TYPES.FRONTEND_METRIC,
    event_type,
  });

/**
 * Send everything still within its TTL, oldest first, then clear the buffer.
 * Called when a socket is registered; a send failure drops the batch rather
 * than retrying, since this is best-effort telemetry.
 */
const flushQueue = (ws: WebSocket): void => {
  if (queue.length === 0) return;
  const now = Date.now();
  const pending = queue;
  queue = [];
  for (const event of pending) {
    if (now - event.queuedAt > QUEUED_EVENT_TTL_MS) continue;
    try {
      ws.send(serialize(event.event_type));
    } catch {
      // Socket went away mid-flush. Telemetry is best-effort; don't re-queue.
      return;
    }
  }
};

export const setGlobalWebSocket = (ws: WebSocket | null) => {
  globalWs = ws;
  if (ws?.readyState === WebSocket.OPEN) flushQueue(ws);
};

export interface LogEventOptions {
  /**
   * Hold the event until a socket is available instead of dropping it.
   *
   * Needed for events that fire *because* the socket is gone. The clearest case
   * is auth loss: flipping `authenticated` to false unmounts the tree that owns
   * the WebSocket, so `AUTH_SESSION_RECOVERED` is emitted while the socket is
   * down and the replacement hasn't opened yet. Without queueing that event can
   * never be delivered, which made recovery look like it never happened
   * (auth_session_lost fired with the socket still up; recovered never did).
   *
   * Deliberately opt-in: hot-path events like the poll heartbeat must keep
   * being dropped, since queueing them would accumulate during exactly the
   * degraded conditions where memory matters.
   */
  durable?: boolean;
}

export const useMetrics = () => {
  /**
   * Emit a frontend metric over the chat WebSocket.
   *
   * Fire-and-forget by default: if the socket isn't open the event is dropped
   * rather than queued, so callers on hot paths (the shoulder-tap poll loop)
   * never block or accumulate memory when the socket is down. Pass
   * `{ durable: true }` for rare events that must survive a socket gap.
   *
   * Only `event_type` travels on the wire. The service's `handle_frontend_metric`
   * forwards exactly `(event_type, contact_id)` to DataSpring, which emits from a
   * fixed field list — so any extra payload here would be silently discarded.
   * Richer per-event context belongs in client-side breadcrumbs (see
   * `utils/diagnostics`), not on this channel.
   */
  const logEvent = (event_type: string, options?: LogEventOptions) => {
    if (globalWs?.readyState === WebSocket.OPEN) {
      globalWs.send(serialize(event_type));
      return;
    }
    if (!options?.durable) return;
    queue.push({ event_type, queuedAt: Date.now() });
    // Trim from the front: if we ever hit the cap, the newest events are the
    // ones worth keeping.
    if (queue.length > MAX_QUEUED_EVENTS) queue = queue.slice(-MAX_QUEUED_EVENTS);
  };

  return { logEvent };
};

/** Test seam: drop queued events so each test starts from a clean module. */
export const __resetMetricsQueueForTests = (): void => {
  queue = [];
};
